import { useCallback, useRef } from "react";

import { History, Location } from "history";
import isFunction from "lodash-es/isFunction";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

type PromptResult = {
  confirmLeave: () => void;
  handlePromptMessage: (location: Location) => boolean;
  hide: () => void;
  visible: boolean;
};

interface UseChangeLocationPromptArgs {
  history: History;
  onLeave?: () => void;
  shouldCloseOnLeave?: boolean;
  shouldShowPrompt: ((location: Location) => boolean) | boolean;
}

const useChangeLocationPrompt = ({
  history,
  onLeave,
  shouldCloseOnLeave,
  shouldShowPrompt,
}: UseChangeLocationPromptArgs): PromptResult => {
  const [visible, show, hide] = useBoolean();
  const targetLocation = useRef<Location | undefined>();
  const confirmLeave = useCallback(() => {
    if (shouldCloseOnLeave) {
      hide();
      onLeave?.();
    }
    if (targetLocation.current) {
      history.push(targetLocation.current);
    }
  }, [hide, history, onLeave, targetLocation, shouldCloseOnLeave]);
  const handlePromptMessage = useCallback(
    (location: Location) => {
      if (isFunction(shouldShowPrompt) ? shouldShowPrompt(location) : shouldShowPrompt) {
        targetLocation.current = location;
        show();
        return false;
      }

      return true;
    },

    [shouldShowPrompt, show, targetLocation]
  );
  return { confirmLeave, handlePromptMessage, hide, visible };
};

export default useChangeLocationPrompt;
